import { ArrowDownTrayIcon, QrCodeIcon } from '@heroicons/react/24/solid';
import { Button, Card, CardBody, CardHeader } from '@heroui/react';
import { QRCodeSVG } from 'qrcode.react';
import { type ComponentProps, useCallback, useRef } from 'react';
import toast from '@/app/toast';

interface AccountQrCodeProps {
  accountKey: string;
  accountName: string;
}

const qrCodeSize = 200;
const qrCodeImageSettings: ComponentProps<typeof QRCodeSVG>['imageSettings'] = {
  src: '/qr-code-logo.png',
  width: 45,
  height: 30,
  excavate: true,
};

export default function AccountQrCode({ accountKey, accountName }: AccountQrCodeProps) {
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const connectUrl = `${window.location.origin}/connect/${accountKey}`;

  const handleDownload = useCallback(async () => {
    if (!qrCodeRef.current || !accountName) {
      return;
    }

    const qrCodeSvgElement = qrCodeRef.current.querySelector('svg');
    if (!qrCodeSvgElement) {
      return;
    }

    const filenameSafeAccountName = accountName
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();

    const qrCodeFilename = `meetingmd-qr-code-${filenameSafeAccountName}.png`;

    try {
      await downloadQrCode(qrCodeFilename, qrCodeSvgElement);
    } catch (error) {
      toast.error('Error downloading QR code. Please try again.');
      console.error('Error downloading QR code:', error);
    }
  }, [accountName]);

  if (!accountKey || !accountName) {
    return null;
  }

  return (
    <Card>
      <CardHeader className='flex gap-2 items-center px-4 pt-4'>
        <QrCodeIcon className='size-5' />
        <span className='font-semibold'>Office QR Code</span>
      </CardHeader>
      <CardBody className='flex flex-col items-center gap-4 px-4 pb-4'>
        <p className='text-sm text-gray-600 text-center'>
          Display this QR code in your office so representatives can scan it to connect with your
          account.
        </p>
        <div ref={qrCodeRef} className='bg-white p-4 rounded-lg'>
          <QRCodeSVG
            value={connectUrl}
            size={qrCodeSize}
            imageSettings={qrCodeImageSettings}
            level='M'
          />
        </div>
        <Button
          color='primary'
          variant='flat'
          startContent={<ArrowDownTrayIcon className='size-4' />}
          onPress={handleDownload}
        >
          Download QR Code
        </Button>
      </CardBody>
    </Card>
  );
}

async function downloadQrCode(filename: string, qrCodeSvgElement: SVGSVGElement) {
  const canvas = document.createElement('canvas');
  const size = 1024;
  canvas.width = size;
  canvas.height = size;

  const canvasContext = canvas.getContext('2d');
  if (!canvasContext) {
    throw new Error('Could not get canvas context');
  }

  // Fill background with white.
  canvasContext.fillStyle = '#ffffff';
  canvasContext.fillRect(0, 0, size, size);

  // Draw QR code onto canvas.
  const qrCodeSvg = new XMLSerializer().serializeToString(qrCodeSvgElement);
  const qrCodeImageElement = await loadImage(`data:image/svg+xml;base64,${btoa(qrCodeSvg)}`);
  canvasContext.drawImage(qrCodeImageElement, 0, 0, size, size);

  // Draw logo onto canvas.
  const logoImageElement = await loadImage(qrCodeImageSettings.src);
  const scale = size / qrCodeSize;
  const logoWidth = qrCodeImageSettings.width * scale;
  const logoHeight = qrCodeImageSettings.height * scale;
  const logoX = (size - logoWidth) / 2;
  const logoY = (size - logoHeight) / 2;
  canvasContext.drawImage(logoImageElement, logoX, logoY, logoWidth, logoHeight);

  // Trigger download.
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const imageElement = new Image();
    imageElement.src = src;
    imageElement.onload = () => resolve(imageElement);
    imageElement.onerror = err => reject(err);
  });
}
